import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import { AppDispatch, RootState } from "app/store";
import { AppTooltip } from "components/AppTooltip/AppTooltip";
import { setShowWhatsNew } from "features/Core/Authentification/state/authentificationSlice";
import {
    GetCurrentPatchNotes,
    MarkPatchNotesAsSeen,
    acknowledgeWhatsNew,
    closeWhatsNewModal,
    openWhatsNewModal,
} from "../state/patch-notes-slice";
import ReleaseEntryList from "./ReleaseEntryList";
import { formatReleaseDate } from "../patch-notes-utils";

const TITLE_ID = 'pn-whats-new-title';

/**
 * "What's New" — shown automatically after an update, and reopenable from the floating button
 * while the user has only set it aside. Mounted once at the app shell (App.tsx) rather than
 * inside a route, so it can appear on whatever page the user lands on after login.
 *
 * Freely dismissible, unlike the Terms modal: closing it is always allowed, and a failed
 * acknowledgement write only means the user sees the release again next login.
 *
 * Two distinct close paths, deliberately: X / backdrop sets it aside and keeps the floating
 * reopen button, "Got it" acknowledges and takes the button away. Both persist the same
 * mark-seen. The nav entry is not a third way back into the modal — it routes to the full
 * history page.
 */
export default function WhatsNewModal() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const hasLoginData = useSelector((state: RootState) => state.authentificationSlice.hasLoginData);
    const showWhatsNew = useSelector((state: RootState) => state.authentificationSlice.showWhatsNew);
    const termsAcceptanceRequired = useSelector((state: RootState) => state.authentificationSlice.termsAcceptanceRequired);
    const dateFormat = useSelector((state: RootState) => state.authentificationSlice.userSettings?.dateFormat);
    const { current, currentLanguage, isLoadingCurrent, currentError, modalOpen, dismissedThisSession } =
        useSelector((state: RootState) => state.patchNotesSlice);

    const language = i18n.language;

    // Only fetch when the server says there is something to show, or when the user reopens it
    // deliberately — never unconditionally on every page load.
    // Terms outranks patch notes: nothing else should be competing for attention while the
    // user is behind a modal they cannot dismiss.
    const shouldAutoOpen = hasLoginData && showWhatsNew && !dismissedThisSession && !termsAcceptanceRequired;

    useEffect(() => {
        if (shouldAutoOpen && !modalOpen) {
            dispatch(openWhatsNewModal());
        }
    }, [shouldAutoOpen, modalOpen, dispatch]);

    // Refetches on a language switch as well as on first open: the descriptions are resolved to
    // one language server-side, so what is cached is only valid for the language it was asked
    // for - re-rendering it would leave the notes in the old language while every other string
    // on screen had already switched.
    //
    // `currentLanguage` is recorded on a failed fetch too, so the "language differs" arm cannot
    // retry in a loop against a failing endpoint; the error state offers the retry button
    // instead.
    useEffect(() => {
        const staleLanguage = currentLanguage !== language;
        const neverFetched = current === null && currentError === '';

        if (modalOpen && !isLoadingCurrent && (staleLanguage || neverFetched)) {
            dispatch(GetCurrentPatchNotes(language));
        }
    }, [modalOpen, current, currentLanguage, language, isLoadingCurrent, currentError, dispatch]);

    // Every close path persists the acknowledgement, so the release is never shown again on a
    // later login regardless of how the user got out of the modal. Close first, then persist:
    // if the write fails the user is still let through.
    const persistSeen = () => {
        dispatch(setShowWhatsNew(false));

        if (current !== null) {
            dispatch(MarkPatchNotesAsSeen(current.version));
        }
    };

    /** X, backdrop, Esc — set aside. The floating reopen button stays offered. */
    const setAside = () => {
        dispatch(closeWhatsNewModal());
        persistSeen();
    };

    /** "Got it" — the user says they read it, so stop offering to reopen it. */
    const acknowledge = () => {
        dispatch(acknowledgeWhatsNew());
        persistSeen();
    };

    // Acknowledging, not setting aside: the user is on their way to read the full history, and
    // a reopen button floating over the Patch Notes page offers to reopen what is already
    // on screen behind it.
    const viewAll = () => {
        acknowledge();
        navigate('/patch-notes');
    };

    const retry = () => {
        dispatch(GetCurrentPatchNotes(language));
    };

    if (!modalOpen) {
        return null;
    }

    return (
        <Dialog
            open={modalOpen}
            onClose={setAside}
            scroll={'paper'}
            aria-labelledby={TITLE_ID}
            className='pn-modal pn-scope'>

            <div className="pn-modal-header">
                <AppTooltip title={t('Close')}>
                    <button type="button" id="pn-close-btn" className="pn-modal-close"
                        aria-label={t('Close')!} onClick={setAside}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </AppTooltip>

                {/* The icon tile carries no information — it is what makes an announcement
                    read as an announcement rather than as an error dialog. Same megaphone as
                    the nav entry and the floating reopen button, so the three read as one
                    feature. */}
                <div className="pn-modal-eyebrow">
                    <span className="pn-modal-icon" aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 11l18-5v12L3 14v-3z" />
                            <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
                        </svg>
                    </span>
                    {current !== null && (
                        <span className="pn-badge pn-badge-latest">
                            {t('patchNotes.newRelease')} · {formatReleaseDate(current.releaseDate, dateFormat)}
                        </span>
                    )}
                </div>

                <h2 id={TITLE_ID} className="pn-modal-title">{t('patchNotes.title')}</h2>
                <p className="pn-modal-intro">{t('patchNotes.modalIntro')}</p>
            </div>

            <DialogContent dividers={false} className="pn-modal-body">
                {isLoadingCurrent && (
                    <div className="pn-modal-state"><CircularProgress size={28} /></div>
                )}

                {!isLoadingCurrent && currentError !== '' && (
                    <div className="pn-modal-state">
                        <p>{t('patchNotes.loadError')}</p>
                        <Button id="pn-retry-btn" className="btn-modal" onClick={retry}>
                            {t('patchNotes.retry')}
                        </Button>
                    </div>
                )}

                {!isLoadingCurrent && currentError === '' && current !== null && current.entries.length > 0 && (
                    <ReleaseEntryList entries={current.entries} />
                )}

                {/* A published release with no renderable entries means the content file is
                    incomplete — say so rather than showing an empty modal. */}
                {!isLoadingCurrent && currentError === '' && (current === null || current.entries.length === 0) && (
                    <div className="pn-modal-state">{t('patchNotes.empty')}</div>
                )}
            </DialogContent>

            {/* Unequal weights on purpose: "Got it" is the primary action and the only filled
                control, "View all patch notes" is a secondary route out. */}
            <DialogActions className="pn-modal-footer">
                <Button id="pn-view-all-btn" className="btn-modal pn-modal-link" onClick={viewAll}
                    endIcon={
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                    }>
                    {t('patchNotes.viewAll')}
                </Button>
                <Button id="pn-got-it-btn" type="submit" className="btn-modal pn-modal-confirm" onClick={acknowledge}>
                    {t('patchNotes.gotIt')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
